/* oxlint-disable lingui/no-unlocalized-strings -- TODO(i18n): translate (debt) */
import { Show } from "solid-js";
import { Copy, Eye, Link as LinkIcon, Trash2 } from "@fleet/icons";
import { Button } from "@/components/ui/button";
import { Badge, Card, CardContent, Switch } from "@fleet/ui";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { cn, type components } from "@time-tracker/shared";

type ThemeRow = components["schemas"]["ThemeDTO"];

interface ThemeCardProps {
  theme: ThemeRow;
  onToggle: (id: string, active: boolean) => void;
  onDelete: (id: string) => void;
  onDuplicate: (id: string) => void;
  isAnyPending: boolean;
  /** `content.write`: without it, row controls are inert and deletion
   *  (destructive, reports nothing) disappears instead of being disabled. */
  canWrite: boolean;
  /**
   * Pre-existing i18n debt (see disable above): this panel translates with PLAIN
   * strings, not macros — Lingui extractor never sees them, so they stay in
   * English across 10 non-English locales. The type barrier of `useI18n().t` rejects
   * passing a string here; `tDynamic` is the EXPLICIT escape to avoid pretending this is
   * already translated. Do not convert to `t` without first wrapping each literal in `msg\`…\``
   * and adding it to the catalog — that is the real debt, not this signature.
   */
  tDynamic: (s: string, v?: Record<string, unknown>) => string;
}

const ID_PREVIEW_LENGTH = 8;

export function ThemeCard(props: ThemeCardProps) {
  const handleToggleChange = (v: boolean) => {
    props.onToggle(props.theme.id, v);
  };

  const handleDuplicateClick = () => {
    props.onDuplicate(props.theme.id);
  };

  const handleDeleteConfirm = () => {
    props.onDelete(props.theme.id);
  };

  return (
    <Card class="group overflow-hidden transition-all hover:border-primary/50 hover:shadow-lg">
      <CardContent class="p-0">
        {/* Preview Strip */}
        <div
          class="h-24 w-full relative flex items-end p-3"
          style={{ "background-color": props.theme.background }}
        >
          <div class="flex gap-2">
            <div
              class="h-8 w-8 rounded-full border-2 border-white/20"
              style={{ "background-color": props.theme.accent }}
            />
            <div
              class="h-8 w-8 rounded-full border-2 border-white/20"
              style={{ "background-color": props.theme.surface }}
            />
          </div>
          <Badge
            variant="secondary"
            class="absolute top-3 right-3 bg-white/10 backdrop-blur-md border-white/20 text-white"
          >
            {props.theme.isDark ? props.tDynamic("🌙 Dark") : props.tDynamic("☀️ Light")}
          </Badge>
        </div>

        <div class="p-4 space-y-4">
          <div class="flex items-start justify-between gap-2">
            <div class="min-w-0">
              <h3 class="font-bold truncate text-lg">{props.theme.name}</h3>
              <p class="text-[10px] text-muted-foreground font-mono">
                {props.tDynamic("ID: {{id}}...", {
                  id: props.theme.id.slice(0, ID_PREVIEW_LENGTH),
                })}{" "}
                • {props.tDynamic("Order")}: {props.theme.sortOrder}
              </p>
            </div>
            <Switch
              checked={props.theme.isActive}
              onCheckedChange={handleToggleChange}
              disabled={props.isAnyPending}
            />
          </div>

          <div class="flex items-center justify-between pt-2 border-t">
            <div class="flex gap-1">
              <Show when={props.canWrite}>
                <Button
                  variant="ghost"
                  size="icon"
                  class="h-8 w-8"
                  aria-label={props.tDynamic("Duplicate theme")}
                  onClick={handleDuplicateClick}
                  disabled={props.isAnyPending}
                >
                  <Copy class="h-4 w-4" />
                </Button>
              </Show>
              <Button variant="ghost" size="icon" class="h-8 w-8">
                <LinkIcon
                  class={cn(
                    "h-4 w-4",
                    props.theme.pairId ? "text-primary" : "text-muted-foreground opacity-50",
                  )}
                />
              </Button>
            </div>
            <div class="flex gap-1">
              <Button variant="ghost" size="icon" class="h-8 w-8">
                <Eye class="h-4 w-4" />
              </Button>
              <Show when={props.canWrite}>
                <ConfirmDialog
                  title={props.tDynamic("Delete theme?")}
                  description={props.tDynamic(
                    'Are you sure you want to delete the theme "{{name}}"? This action cannot be undone.',
                    { name: props.theme.name },
                  )}
                  confirmLabel={props.tDynamic("Delete")}
                  variant="destructive"
                  onConfirm={handleDeleteConfirm}
                  renderTrigger={(open) => (
                    <Button
                      variant="ghost"
                      size="icon"
                      class="h-8 w-8 text-destructive hover:bg-destructive/10 hover:text-destructive"
                      aria-label={props.tDynamic("Delete theme")}
                      onClick={open}
                      disabled={props.isAnyPending}
                    >
                      <Trash2 class="h-4 w-4" />
                    </Button>
                  )}
                />
              </Show>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
